import { BankAccountType, Country } from '@commenda/prisma';

/** Mirrors the backend `TaxAndAccountingOnboardingStatus`. */
export enum TaxAndAccountingOnboardingStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

/** One attested director entry (mirrors backend `TComplianceDirector`). `nationality` (one or more citizenships) drives `hasForeignDirector`. */
export type TComplianceDirectorAnswer = {
  nationality?: Country[];
  jobTitle?: string;
  isResidentDirector?: boolean;
  usResidencyStatus?: string;
  /** Display-only conveniences for the key-person picker; never used in derivation. */
  firstName?: string;
  lastName?: string;
};

/** One attested bank-account entry (mirrors backend `TComplianceBankAccount`). `country` drives `hasForeignBankAccount`. */
export type TComplianceBankAccountAnswer = {
  country?: Country;
  currency?: string;
  bankAccountType?: BankAccountType;
  bankName?: string;
};

/** The form offers only the three tax classifications representable as a scalar fact. */
export type TaxClassificationAnswer =
  | 'C_CORPORATION'
  | 'S_CORPORATION'
  | 'PARTNERSHIP';
export type RevenueBandAnswer =
  | 'PRE_REVENUE'
  | 'UNDER_1M_USD'
  | 'MID_1M_10M_USD'
  | 'LARGE_10M_PLUS_USD';

export type TTaxAndAccountingOnboardingAnswers = {
  EMPLOYEE_COUNT?: number;
  REVENUE_BAND?: RevenueBandAnswer;
  TAX_CLASSIFICATION?: TaxClassificationAnswer;
  IS_VAT_GST_REGISTERED?: boolean;
  PAYS_INDEPENDENT_CONTRACTORS?: boolean;
  HAS_NON_RESIDENT_PAYMENTS?: boolean;
  HAS_STOCK_BASED_COMPENSATION_PLAN?: boolean;
  HAS_FOREIGN_SUBSIDIARY?: boolean;
  DIRECTORS?: TComplianceDirectorAnswer[];
  BANK_ACCOUNTS?: TComplianceBankAccountAnswer[];
};

/** A carry-forward pre-fill value, tagged by kind (mirrors backend `TTaxAndAccountingOnboardingPrefillValue`). */
export type TTaxAndAccountingOnboardingPrefillValue =
  | { type: 'number'; value: number }
  | { type: 'revenue-band'; value: RevenueBandAnswer }
  | { type: 'tax-classification'; value: TaxClassificationAnswer }
  | { type: 'boolean'; value: boolean }
  | { type: 'directors'; value: TComplianceDirectorAnswer[] }
  | { type: 'bank-accounts'; value: TComplianceBankAccountAnswer[] };

export type TTaxAndAccountingOnboardingPrefillField = {
  value: TTaxAndAccountingOnboardingPrefillValue;
  sourceFiscalYear: number;
};

/** Per-field carry-forward pre-fill keyed by fact id (only present fields appear). */
export type TTaxAndAccountingOnboardingPrefill = Partial<
  Record<
    keyof TTaxAndAccountingOnboardingAnswers,
    TTaxAndAccountingOnboardingPrefillField
  >
>;

export type TTaxAndAccountingOnboarding = {
  corporationId: number;
  fiscalYear: number;
  status: TaxAndAccountingOnboardingStatus;
  answers: TTaxAndAccountingOnboardingAnswers | null;
  prefilledValues: TTaxAndAccountingOnboardingPrefill;
};

/** Raw wizard state persisted for resume (mirrors backend `ITaxAndAccountingOnboardingDraft`). */
export type TTaxAndAccountingOnboardingDraft = {
  formData: Record<string, unknown>;
  lastStepIndex: number;
  assigneeUserIds?: string[];
};

/** The corp's single resumable (in-progress) onboarding, or `draft: null` when there is none. */
export type TTaxAndAccountingOnboardingResume = {
  fiscalYear: number | null;
  status: TaxAndAccountingOnboardingStatus;
  draft: TTaxAndAccountingOnboardingDraft | null;
};
